/* ═══════════════════════════════════════════════════════════════════════════
   THE SALE, AS A DOCUMENT — and the trail that leads back to it

   One completed piece of work: the treatment, the products that went over
   the counter with it, who did it, what it came to, how it was paid, and the
   reference that identifies it for ever after.

   ── The two-way trail ────────────────────────────────────────────────────

   Bokføringsloven §§ 4 nr. 7 and 6: a booked entry must be traceable from
   its documentation through to the reported figures, AND from the reported
   figure back to the entry it came from. Both directions. A report that only
   totals upwards is half of it, and the half that is missing is the half an
   auditor asks for.

   What makes the downward direction possible is a SEQUENTIAL reference on
   every sale. The number is what a report points back down at and what a
   sale points up with, and a gap in the series is the first thing anybody
   checking will look for — so `gaps()` reports them rather than hiding them,
   and `reference()` produces the series rather than a random id.

   ── A remote sale, not a till ────────────────────────────────────────────

   Bokføringsforskriften § 5-3-1 bokstav a defines kontantsalg as a sale
   settled AT DELIVERY by card or cash — and the same letter says outright
   that a sale over the internet is not kontantsalg. A booking paid for on
   Avtale.net is a remote sale: the money moves when the customer books, days
   before anybody delivers anything.

   So the kassasystem rules do not reach this. What a remote sale owes is an
   ordinary salgsdokument under bokføringsforskriften kap. 5 — seller, date,
   sequential number, lines, sums with VAT by rate — and that is exactly what
   `salesDoc` produces and `missingFields` audits.

   An earlier version of this file claimed the opposite, and said the platform
   would owe Skatteetaten a produkterklæring once it took payment. That was
   wrong, and wrong in the direction that matters: it told a tenant they had a
   compliance problem they do not have. `boundary()` is what replaced it, and
   it states the one line that IS true — a customer paying at the counter on
   arrival is a cash sale and belongs in the salon's own till.

   ── Who is selling ───────────────────────────────────────────────────────

   The tenant, always, and that is not a formality on this document — it is
   the architecture. Each business holds its OWN merchant credentials with
   Vipps, Stripe or whoever takes the card; the money moves from the customer
   to that merchant account and never touches a platform account. See
   domain/payments.js. Avtale.net is the digital infrastructure between them
   and is nobody's seller.

   Which is why `salesDoc` takes the BUSINESS and puts its name and
   organisation number in `seller` — and reports `seller` as a missing field
   rather than substituting the platform's own details, because a sales
   document naming the wrong party is not a lesser document, it is the wrong
   document.
   ═══════════════════════════════════════════════════════════════════════ */
import { rules as rulesFor, HOME } from '../law/index.js';
import { splitGross, rateOf } from './journal.js';

const round2 = n => Math.round(Number(n || 0) * 100) / 100;

/**
 * The reference a sale carries for ever.
 *
 * Year and a sequence, zero-padded so the series sorts as text as well as it
 * sorts as a number: `2026-000148`. Per business and per year, which is how
 * an accountant expects a series to restart, and never a random id — a
 * reference you cannot read a position out of tells nobody whether anything
 * is missing between two of them.
 */
export const reference = (year, n) =>
  `${year}-${String(Math.max(1, Number(n) || 1)).padStart(6, '0')}`;

export const yearOfRef = ref => Number(String(ref || '').slice(0, 4)) || null;
export const seqOfRef = ref => Number(String(ref || '').slice(5)) || null;

/**
 * Holes in the series.
 *
 * Reported, loudly, rather than smoothed over. A missing reference is either
 * a sale that was removed or a system that lost one, and both are things
 * somebody has to be told about — a report that silently renumbered would be
 * hiding exactly what the numbering exists to reveal.
 */
export function gaps(refs = []) {
  const byYear = {};
  refs.filter(Boolean).forEach(r => {
    const y = yearOfRef(r);
    const n = seqOfRef(r);
    if (!y || !n) return;
    (byYear[y] = byYear[y] || []).push(n);
  });

  const out = [];
  Object.keys(byYear).sort().forEach(y => {
    const list = [...new Set(byYear[y])].sort((a, b) => a - b);
    for (let i = 1; i < list.length; i += 1) {
      const step = list[i] - list[i - 1];
      if (step > 1) {
        out.push({
          year: Number(y),
          after: reference(y, list[i - 1]),
          before: reference(y, list[i]),
          missing: step - 1,
        });
      }
    }
  });
  return out;
}

/* ── What a sale is made of ───────────────────────────────────────────── */

/**
 * The lines of one sale, as a document.
 *
 * The treatment and anything that went over the counter with it are separate
 * lines on purpose: they can carry different VAT rates, and a salon selling
 * a bottle of shampoo alongside a haircut is making two different kinds of
 * sale. Rolling them into one figure loses the split the journal needs.
 */
export function linesOf(sale = {}) {
  const out = [];
  if (sale.service) {
    out.push({
      kind: 'service',
      what: sale.service,
      qty: 1,
      each: Number(sale.serviceAmount ?? sale.amount) || 0,
      vat: sale.serviceVat || null,
    });
  }
  (sale.products || []).forEach(p => {
    out.push({
      kind: 'product',
      what: p.name,
      qty: Number(p.qty) || 1,
      each: Number(p.price) || 0,
      vat: p.vat || null,
    });
  });
  return out.map(l => ({ ...l, sum: round2(l.each * l.qty) }));
}

/**
 * The whole document: lines, VAT by rate, total, and everything a receipt is
 * required to carry.
 *
 * `missingFields` is the honest half. A rate that has not been set is not
 * guessed — see domain/journal.js — so a document can be complete as a
 * record of what happened and still not be complete as a receipt, and the
 * screen has to be able to say which.
 */
export function salesDoc(sale = {}, business = {}, country = HOME) {
  const law = rulesFor(country).books;
  const lines = linesOf(sale);
  const gross = round2(lines.reduce((n, l) => n + l.sum, 0));

  const byRate = {};
  let rated = 0;
  lines.forEach(l => {
    const r = l.vat ? rateOf(l.vat, country) : null;
    if (!r) return;
    rated += l.sum;
    const part = splitGross(l.sum, r.pct);
    const row = byRate[r.id] || (byRate[r.id] = {
      id: r.id, pct: r.pct, label: r.label, gross: 0, net: 0, vat: 0,
    });
    row.gross = round2(row.gross + part.gross);
    row.net = round2(row.net + part.net);
    row.vat = round2(row.vat + part.vat);
  });

  const missingFields = [];
  if (!sale.ref) missingFields.push('number');
  if (!business.org_no) missingFields.push('seller');
  if (!sale.at) missingFields.push('when');
  if (!lines.length) missingFields.push('lines');
  if (round2(rated) !== gross) missingFields.push('sums');
  if (!sale.paid) missingFields.push('paid');

  return {
    ref: sale.ref || null,
    at: sale.at || null,
    staff: sale.staff || null,
    customer: sale.customer || null,
    paid: sale.paid || null,
    lines,
    vat: Object.values(byRate).sort((a, b) => b.pct - a.pct),
    gross,
    /* What is inside the total, and what is not yet rated. The difference is
       visible on purpose. */
    ratedGross: round2(rated),
    unratedGross: round2(gross - rated),
    seller: {
      name: business.name || null,
      org_no: business.org_no || null,
      /* "MVA" after the number where the business is registered. It is a
         required part of the line and it is a claim — so it follows the
         business's own answer and is never added because the number looks
         like one. */
      vatRegistered: !!business.vat_registered,
    },
    missingFields,
    complete: missingFields.length === 0,
    needs: law.receiptNeeds,
    cite: law.receiptCite,
  };
}

/** The label for a field a receipt is missing, from the rule set. */
export const fieldWord = (id, country = HOME) =>
  (rulesFor(country).books.receiptNeeds.find(([k]) => k === id) || [])[1] || id;

/* ── The report ───────────────────────────────────────────────────────── */

/**
 * A period's sales, as the report a business would print or hand over.
 *
 * Every row carries its reference, so the trail runs downwards from the
 * total to the sale; every sale carries the period it was counted in, so it
 * runs upwards as well. That pair is the whole of § 6.
 */
export function report(sales = [], { from, to, business = {}, country = HOME } = {}) {
  const law = rulesFor(country).books;
  const docs = sales
    .filter(s => !s.demoOnly)
    .map(s => ({ ...salesDoc(s, business, country), id: s.id }))
    .sort((a, b) => String(a.ref || '').localeCompare(String(b.ref || '')));

  const byRate = {};
  docs.forEach(d => d.vat.forEach(v => {
    const row = byRate[v.id] || (byRate[v.id] = { ...v, gross: 0, net: 0, vat: 0 });
    row.gross = round2(row.gross + v.gross);
    row.net = round2(row.net + v.net);
    row.vat = round2(row.vat + v.vat);
  }));

  const paid = {};
  docs.forEach(d => {
    const k = d.paid || 'not recorded';
    paid[k] = round2((paid[k] || 0) + d.gross);
  });

  return {
    from, to,
    business: business.name || null,
    org_no: business.org_no || null,
    docs,
    count: docs.length,
    gross: round2(docs.reduce((n, d) => n + d.gross, 0)),
    vat: Object.values(byRate).sort((a, b) => b.pct - a.pct),
    /* By payment method, because that is what reconciles against the bank
       and the till, and because it is a required line on the document. */
    paid: Object.entries(paid).map(([how, kr]) => ({ how, kr })),
    /* The references the period covers, and any holes in them. */
    first: docs[0]?.ref || null,
    last: docs[docs.length - 1]?.ref || null,
    gaps: gaps(docs.map(d => d.ref)),
    incomplete: docs.filter(d => !d.complete).length,
    trace: law.traceWhat,
    traceCite: law.traceCite,
    keep: law.keepWhat,
    keepCite: law.keepCite,
  };
}

/**
 * Where this document stops, and the tenant's own till begins.
 *
 * On the screen, under the report. Not a disclaimer about a rule this
 * platform breaks — it breaks none — but a boundary a salon owner has to
 * know, because the two kinds of sale are settled in different places and
 * only one of them is ours.
 */
export function boundary(country = HOME) {
  const law = rulesFor(country).books;
  return {
    what: 'This is a sales document for a remote sale. A booking paid for '
      + 'here is settled over the internet, which is not a cash sale — so the '
      + 'cash register rules do not apply to it.',
    why: law.remoteWhat,
    so: law.remoteSo,
    cite: law.remoteCite,
    /* Who is selling, and why the answer is never us. Each business holds
       its own merchant credentials, so the money goes from the customer to
       the business's own account without passing through a platform one.
       This platform is the infrastructure between them. */
    whoSells: 'You are. Every sale here is yours: your name and organisation '
      + 'number on the document, your own merchant account at the payment '
      + 'provider, and the money moving from your customer to you without '
      + 'passing through an Avtale.net account. We are the infrastructure '
      + 'between the two, and never a party to the sale.',
    /* The half that is the tenant's, said plainly so they do not read this
       screen as covering it. */
    till: law.tillLine,
    tillWho: law.tillWho,
    tillCite: law.tillCite,
  };
}

/** What the report gives, said plainly. */
export const gives = (country = HOME) => {
  const law = rulesFor(country).books;
  return [
    ['A reference on every sale', 'Sequential, per year, so a hole in the series shows.'],
    ['Both directions', law.traceWhat],
    ['The sales document fields', law.receiptNeeds.map(([, w]) => w).join('; ') + '.'],
    ['Kept', law.keepWhat],
  ];
};
